import { Component } from '@theme/component';

class HyruleGallery extends Component {
  controller;

  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-hm-view]')) this.selectView(event);
      else if (event.target.closest('[data-hm-prev]')) this.previousView();
      else if (event.target.closest('[data-hm-next]')) this.nextView();
    }, { signal: this.controller.signal });
    this.setView(Number(this.dataset.view) || 0);
  }

  updatedCallback() {
    super.updatedCallback();
    this.setView(Number(this.dataset.view) || 0);
  }

  disconnectedCallback() {
    this.controller?.abort();
    super.disconnectedCallback();
  }

  selectView(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-hm-view]') : null;
    if (target) this.setView(Number(target.getAttribute('data-hm-view')));
  }

  previousView() {
    this.setView((Number(this.dataset.view) + 2) % 3);
  }

  nextView() {
    this.setView((Number(this.dataset.view) + 1) % 3);
  }

  setView(view) {
    if (!Number.isInteger(view) || view < 0 || view > 2) return;
    this.dataset.view = String(view);
    for (const button of this.querySelectorAll('[data-hm-view]')) {
      button.setAttribute('aria-pressed', String(Number(button.getAttribute('data-hm-view')) === view));
    }
  }
}

if (!customElements.get('hyrule-gallery-component')) {
  customElements.define('hyrule-gallery-component', HyruleGallery);
}

class HyruleBundle extends Component {
  controller;
  requestController;
  busy = false;
  disabledStates = new Map();
  labelStates = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    this.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement)) return;
      if (event.target.matches('input[name="hm_bundle"], [data-hm-upsell]')) this.updateSelection();
    }, { signal: this.controller.signal });
    this.addEventListener('submit', (event) => {
      if (!(event.target instanceof HTMLFormElement) || !event.target.matches('.hm-product-form')) return;
      event.preventDefault();
      this.submitSelection();
    }, { signal: this.controller.signal });
    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-hm-submit]');
      if (!(button instanceof HTMLButtonElement) || button.type === 'submit') return;
      event.preventDefault();
      this.submitSelection();
    }, { signal: this.controller.signal });
    this.updateSelection();
  }

  updatedCallback() {
    super.updatedCallback();
    this.updateSelection();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.requestController?.abort();
    super.disconnectedCallback();
  }

  get selectedBundle() {
    return this.querySelector('input[name="hm_bundle"]:checked');
  }

  get selectedAddons() {
    return [...this.querySelectorAll('input[data-hm-upsell]:checked')];
  }

  get submitButtons() {
    const external = this.closest('.hm-storefront')?.querySelectorAll('[data-hm-owner]') || [];
    return [...this.querySelectorAll('[data-hm-submit]'), ...[...external].filter((button) => button.getAttribute('data-hm-owner') === this.id && !this.contains(button))];
  }

  formatMoney(cents) {
    const currency = this.dataset.currency || 'CAD';
    const locale = document.documentElement.lang || 'en-CA';
    return `${new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100)} ${currency}`;
  }

  variantId(input) {
    const value = input?.getAttribute('data-variant-id')?.split('/').pop() || '';
    if (!/^\d+$/.test(value)) return null;
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  }

  canPurchase() {
    const selected = this.selectedBundle;
    return this.dataset.purchaseEnabled === 'true' && selected instanceof HTMLInputElement && selected.dataset.available !== 'false';
  }

  updateSelection() {
    const selected = this.selectedBundle;
    if (!(selected instanceof HTMLInputElement)) return;
    const panel = this.querySelector('.hm-upsell-panel');
    for (const radio of this.querySelectorAll('input[name="hm_bundle"]')) {
      radio.closest('.hm-bundle-card')?.setAttribute('data-selected', String(radio === selected));
    }
    const slot = selected.closest('.hm-bundle-card')?.querySelector('.hm-bundle-panel-slot');
    if (panel && slot && panel.parentElement !== slot) slot.append(panel);
    const cents = Number(selected.dataset.price) || 0;
    const total = this.selectedAddons.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), cents);
    const storefront = this.closest('.hm-storefront');
    for (const target of storefront?.querySelectorAll('[data-hm-pack-price]') || []) target.textContent = this.formatMoney(cents);
    for (const target of storefront?.querySelectorAll('[data-hm-total]') || []) target.textContent = this.formatMoney(total);
    const variantInput = this.querySelector('.hm-product-form input[name="id"]');
    if (variantInput instanceof HTMLInputElement) variantInput.value = String(this.variantId(selected) || '');
    for (const button of this.submitButtons) {
      if (button instanceof HTMLButtonElement) button.disabled = this.busy || !this.canPurchase();
    }
    this.clearError();
  }

  clearError() {
    const message = this.querySelector('[data-hm-error]');
    if (message) message.textContent = '';
  }

  showError(text) {
    const message = this.querySelector('[data-hm-error]');
    if (!message) return;
    message.textContent = text;
    message.setAttribute('tabindex', '-1');
    if (message instanceof HTMLElement) {
      message.scrollIntoView({ block: 'center', behavior: 'auto' });
      message.focus({ preventScroll: true });
    }
  }

  setBusy(busy) {
    this.busy = busy;
    this.setAttribute('aria-busy', String(busy));
    if (busy) {
      for (const input of this.querySelectorAll('input[name="hm_bundle"], input[data-hm-upsell]')) {
        this.disabledStates.set(input, input.disabled);
        input.disabled = true;
      }
      for (const button of this.submitButtons) {
        if (button instanceof HTMLButtonElement) button.disabled = true;
        const label = button.querySelector('[data-hm-button-label]');
        if (label) {
          this.labelStates.set(label, label.textContent);
          label.textContent = this.dataset.addingLabel || label.textContent;
        }
      }
    } else {
      for (const [input, disabled] of this.disabledStates) input.disabled = disabled;
      this.disabledStates.clear();
      for (const [label, value] of this.labelStates) label.textContent = value;
      this.labelStates.clear();
      for (const button of this.submitButtons) {
        if (button instanceof HTMLButtonElement) button.disabled = !this.canPurchase();
      }
    }
  }

  async submitSelection() {
    if (this.busy || !this.canPurchase()) return;
    this.clearError();
    const items = [this.selectedBundle, ...this.selectedAddons].map((input) => ({ id: this.variantId(input), quantity: 1 }));
    if (items.some((item) => !item.id)) {
      this.showError(this.dataset.missingVariantMessage || this.dataset.errorMessage || '');
      return;
    }
    const addUrl = new URL(this.dataset.cartAddUrl || '', window.location.origin);
    const cartUrl = new URL(this.dataset.cartUrl || '', window.location.origin);
    if (!this.dataset.cartAddUrl || !this.dataset.cartUrl || addUrl.origin !== window.location.origin || cartUrl.origin !== window.location.origin) {
      this.showError(this.dataset.errorMessage || '');
      return;
    }
    this.setBusy(true);
    this.requestController = new AbortController();
    let serverError = '';
    let added = false;
    try {
      const response = await fetch(addUrl.href, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
        signal: this.requestController.signal,
      });
      const result = await response.json();
      if (!response.ok) {
        serverError = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : this.dataset.errorMessage || '';
        throw new Error(serverError);
      }
      added = true;
      window.location.assign(cartUrl.href);
    } catch (error) {
      if (error?.name !== 'AbortError') this.showError(serverError || this.dataset.errorMessage || '');
    } finally {
      if (this.isConnected && !added) this.setBusy(false);
    }
  }
}

if (!customElements.get('hyrule-bundle-component')) {
  customElements.define('hyrule-bundle-component', HyruleBundle);
}

function prepareMenu(scope = document) {
  for (const header of scope.querySelectorAll('.hm-header')) {
    const button = header.querySelector('.hm-menu-toggle');
    if (!(button instanceof HTMLButtonElement)) continue;
    header.setAttribute('data-menu-ready', 'true');
    header.setAttribute('data-menu-open', 'false');
    button.setAttribute('aria-expanded', 'false');
  }
}

function closeMenu(header, restoreFocus = false) {
  header.setAttribute('data-menu-open', 'false');
  const button = header.querySelector('.hm-menu-toggle');
  button?.setAttribute('aria-expanded', 'false');
  if (restoreFocus && button instanceof HTMLElement) button.focus();
}

const stickyObservers = new WeakMap();

function prepareSticky(scope = document) {
  for (const storefront of scope.querySelectorAll('.hm-storefront')) {
    const sticky = storefront.querySelector('[data-hm-sticky]');
    const hero = storefront.querySelector('.hm-product-section');
    if (!(sticky instanceof HTMLElement) || !hero) continue;
    stickyObservers.get(storefront)?.disconnect();
    if (!('IntersectionObserver' in window)) continue;
    const observer = new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
    }, { threshold: 0 });
    observer.observe(hero);
    stickyObservers.set(storefront, observer);
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const ownerButton = event.target.closest('[data-hm-owner]');
  if (ownerButton instanceof HTMLButtonElement && !ownerButton.disabled) {
    const owner = document.getElementById(ownerButton.getAttribute('data-hm-owner') || '');
    if (owner instanceof HyruleBundle && !owner.contains(ownerButton)) {
      event.preventDefault();
      owner.submitSelection();
      return;
    }
  }
  const menuButton = event.target.closest('.hm-menu-toggle');
  if (menuButton) {
    const header = menuButton.closest('.hm-header');
    if (!header) return;
    const open = header.getAttribute('data-menu-open') !== 'true';
    header.setAttribute('data-menu-open', String(open));
    menuButton.setAttribute('aria-expanded', String(open));
    return;
  }

  const menuLink = event.target.closest('.hm-nav a');
  if (menuLink) {
    const header = menuLink.closest('.hm-header');
    if (header) closeMenu(header);
  }

  const quantityButton = event.target.closest('[data-hm-quantity]');
  if (!quantityButton) return;
  const input = quantityButton.closest('.hm-quantity')?.querySelector('input[name="quantity"]');
  if (!(input instanceof HTMLInputElement)) return;
  const direction = Number(quantityButton.getAttribute('data-hm-quantity'));
  if (direction !== 1 && direction !== -1) return;
  if (direction > 0) input.stepUp();
  else input.stepDown();
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  for (const header of document.querySelectorAll('.hm-header[data-menu-open="true"]')) closeMenu(header, true);
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    prepareMenu(event.target);
    prepareSticky(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return;
  for (const storefront of event.target.querySelectorAll('.hm-storefront')) {
    stickyObservers.get(storefront)?.disconnect();
  }
});

prepareMenu();
prepareSticky();
